import type { LensRunDto, SessionDetail, UtteranceDto } from "./api.js";

type Emocja = UtteranceDto["emotions"][number];

/** Pola, ktorych serwer nie wysyla userowi; wartosci neutralne, nigdy nazwy modelu. */
const BRAKI_PRZEBIEGU: Pick<LensRunDto, "provider" | "model" | "resultJson" | "latencyMs" | "retries"> = {
  provider: "",
  model: "",
  resultJson: null,
  latencyMs: null,
  retries: 0,
};

const BRAKI_EMOCJI: Pick<Emocja, "provider" | "model"> = { provider: "", model: "" };

/**
 * Szczegol analizy dla konta `user` ma OBCIETE pola techniczne (services/widokDlaKonta.ts na serwerze): klucze `provider`,
 * `model`, `resultJson`, `latencyMs` i `retries` znikaja z przebiegow, a `provider` i `model` z emocji wypowiedzi. Typy
 * klienta (`SessionDetail`) opisuja pelna odpowiedz administratora, wiec kod, ktory czyta te pola bez sprawdzenia (np.
 * `przebieg.model.length` w lib/raportOceny.ts), wywala sie na odpowiedzi usera i caly ekran analizy znika.
 *
 * Ta funkcja dopelnia brakujace pola neutralnymi wartosciami, ZANIM szczegol trafi do reszty klienta. Dopelnia i KLUCZ,
 * ktorego nie ma, i klucz z wartoscia `null` albo `undefined` (semantyka `??`): serwer, ktory kiedys zacznie przesylac
 * `"model": null` zamiast pomijac klucz, nie moze wywrocic ekranu. Wartosci, ktore serwer podal, zostaja bez zmian
 * (administrator dostaje dokladnie to samo, co mial). Pole puste ("" i null) nigdy nie jest pokazywane jako nazwa modelu:
 * widok usera tych pol w ogole nie wypisuje.
 */
export function uzupelnijBrakiDlaUzytkownika(sesja: SessionDetail): SessionDetail {
  return {
    ...sesja,
    sttProvider: sesja.sttProvider ?? null,
    sttModel: sesja.sttModel ?? null,
    expressionProvider: sesja.expressionProvider ?? null,
    expressionModel: sesja.expressionModel ?? null,
    lensRuns: sesja.lensRuns.map(dopelnijPrzebieg),
    utterances: sesja.utterances.map((wypowiedz) => ({
      ...wypowiedz,
      emotions: wypowiedz.emotions.map(dopelnijEmocje),
    })),
  };
}

// Rzutowanie na Partial, bo typ opisuje pelna odpowiedz, a tu wlasnie chodzi o odpowiedz, ktora jej nie jest.
function dopelnijPrzebieg(przebieg: LensRunDto): LensRunDto {
  const surowy = przebieg as Partial<Record<keyof LensRunDto, unknown>>;
  return {
    ...przebieg,
    provider: (surowy.provider as string | null | undefined) ?? BRAKI_PRZEBIEGU.provider,
    model: (surowy.model as string | null | undefined) ?? BRAKI_PRZEBIEGU.model,
    resultJson: surowy.resultJson ?? BRAKI_PRZEBIEGU.resultJson,
    latencyMs: (surowy.latencyMs as number | null | undefined) ?? BRAKI_PRZEBIEGU.latencyMs,
    retries: (surowy.retries as number | null | undefined) ?? BRAKI_PRZEBIEGU.retries,
  };
}

function dopelnijEmocje(emocja: Emocja): Emocja {
  const surowa = emocja as Partial<Record<keyof Emocja, unknown>>;
  return {
    ...emocja,
    provider: (surowa.provider as string | null | undefined) ?? BRAKI_EMOCJI.provider,
    model: (surowa.model as string | null | undefined) ?? BRAKI_EMOCJI.model,
  };
}
